import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { toast } from 'sonner'

export type PaymentStatus = 'pending' | 'paid' | 'overdue' | 'cancelled'
export type PaymentMethod = 'pix' | 'bank_transfer' | 'cash' | 'boleto' | 'credit_card' | 'paypal' | 'other'
export type RecurrenceInterval = 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly'

export interface Payment {
  id: string
  user_id: string
  project_id: string | null
  client_id: string | null
  amount: number
  currency: string
  due_date: string | null
  paid_date: string | null
  status: PaymentStatus
  payment_method: PaymentMethod | null
  description: string | null
  notes: string | null
  invoice_number: string | null
  // Recurring payment fields
  is_recurring: boolean
  recurrence_interval: RecurrenceInterval | null
  // Monthly recurring (new type)
  recurring_day_of_month: number | null
  recurring_parent_id: string | null
  // Installment fields
  current_installment: number | null
  total_installments: number | null
  // Receipt/proof
  receipt_url: string | null
  created_at: string
  updated_at: string
  projects?: {
    id: string
    name: string
    clients: { id: string; name: string } | null
  } | null
}

export interface PaymentInsert {
  project_id?: string | null
  client_id?: string | null
  amount: number
  currency?: string
  due_date?: string | null
  paid_date?: string | null
  status?: PaymentStatus
  payment_method?: PaymentMethod | null
  description?: string | null
  notes?: string | null
  invoice_number?: string | null
  // Recurring payment fields
  is_recurring?: boolean
  recurrence_interval?: RecurrenceInterval | null
  // Monthly recurring (new type)
  recurring_day_of_month?: number | null
  recurring_parent_id?: string | null
  // Installment fields
  current_installment?: number | null
  total_installments?: number | null
  // Receipt/proof
  receipt_url?: string | null
}

export interface PaymentFilters {
  status?: PaymentStatus | null
  project_id?: string | null
  client_id?: string | null
  payment_method?: PaymentMethod[] | null
  start_date?: string | null
  end_date?: string | null
}

export interface PaymentSummary {
  total_pending: number
  total_paid: number
  total_overdue: number
  total_upcoming: number
  count_pending: number
  count_paid: number
  count_overdue: number
  count_upcoming: number
}

export function usePayments(filters?: PaymentFilters) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['payments', filters],
    queryFn: async () => {
      let query = supabase
        .from('payments')
        .select(`
          id,
          user_id,
          project_id,
          client_id,
          amount,
          currency,
          due_date,
          paid_date,
          status,
          payment_method,
          description,
          notes,
          invoice_number,
          is_recurring,
          recurrence_interval,
          recurring_day_of_month,
          recurring_parent_id,
          current_installment,
          total_installments,
          receipt_url,
          created_at,
          updated_at,
          projects (
            id,
            name,
            clients (id, name)
          )
        `)
        .order('due_date', { ascending: true, nullsFirst: false })

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }

      if (filters?.project_id) {
        query = query.eq('project_id', filters.project_id)
      }

      if (filters?.payment_method && filters.payment_method.length > 0) {
        query = query.in('payment_method', filters.payment_method)
      }

      if (filters?.start_date) {
        query = query.gte('due_date', filters.start_date)
      }

      if (filters?.end_date) {
        query = query.lte('due_date', filters.end_date)
      }

      const { data, error } = await query

      if (error) throw error

      // Filter by client_id if provided (needs to be done client-side due to nested filter)
      let result = data as Payment[]
      if (filters?.client_id) {
        result = result.filter(p => p.projects?.clients?.id === filters.client_id)
      }

      return result
    },
    enabled: !!user,
  })
}

export function usePayment(id: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['payments', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          id,
          user_id,
          project_id,
          client_id,
          amount,
          currency,
          due_date,
          paid_date,
          status,
          payment_method,
          description,
          notes,
          invoice_number,
          is_recurring,
          recurrence_interval,
          recurring_day_of_month,
          recurring_parent_id,
          current_installment,
          total_installments,
          receipt_url,
          created_at,
          updated_at,
          projects (
            id,
            name,
            fixed_price,
            billing_type,
            clients (id, name, email)
          )
        `)
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data as Payment | null
    },
    enabled: !!user && !!id,
  })
}

export function usePaymentSummary() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['payments', 'summary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('amount, status, due_date')

      if (error) throw error

      const today = new Date()
      const sevenDaysFromNow = new Date(today)
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)

      const summary: PaymentSummary = {
        total_pending: 0,
        total_paid: 0,
        total_overdue: 0,
        total_upcoming: 0,
        count_pending: 0,
        count_paid: 0,
        count_overdue: 0,
        count_upcoming: 0,
      }

      data?.forEach((payment: { amount: number; status: string; due_date: string | null }) => {
        const amount = Number(payment.amount) || 0
        
        switch (payment.status) {
          case 'pending':
            summary.total_pending += amount
            summary.count_pending++
            // Check if upcoming (next 7 days)
            if (payment.due_date) {
              const dueDate = new Date(payment.due_date)
              if (dueDate >= today && dueDate <= sevenDaysFromNow) {
                summary.total_upcoming += amount
                summary.count_upcoming++
              }
            }
            break
          case 'paid':
            summary.total_paid += amount
            summary.count_paid++
            break
          case 'overdue':
            summary.total_overdue += amount
            summary.count_overdue++
            break
        }
      })

      return summary
    },
    enabled: !!user,
  })
}

export function useUpcomingPayments(daysAhead = 7) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['payments', 'upcoming', daysAhead],
    queryFn: async () => {
      const today = new Date()
      const futureDate = new Date(today)
      futureDate.setDate(futureDate.getDate() + daysAhead)

      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          projects (
            id,
            name,
            clients (id, name)
          )
        `)
        .in('status', ['pending', 'overdue'])
        .lte('due_date', futureDate.toISOString().split('T')[0])
        .order('due_date', { ascending: true })

      if (error) throw error
      return data as Payment[]
    },
    enabled: !!user,
  })
}

export function useProjectPayments(projectId: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['payments', 'project', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          id,
          user_id,
          project_id,
          client_id,
          amount,
          currency,
          due_date,
          paid_date,
          status,
          payment_method,
          description,
          notes,
          invoice_number,
          is_recurring,
          recurrence_interval,
          recurring_day_of_month,
          recurring_parent_id,
          current_installment,
          total_installments,
          receipt_url,
          created_at,
          updated_at
        `)
        .eq('project_id', projectId)
        .order('due_date', { ascending: true })

      if (error) throw error
      return data as Payment[]
    },
    enabled: !!user && !!projectId,
  })
}

export function useProjectPaymentSummary(projectId: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['payments', 'project-summary', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('amount, status')
        .eq('project_id', projectId)

      if (error) throw error

      const summary = {
        total_paid: 0,
        total_pending: 0,
        total_overdue: 0,
        count_paid: 0,
        count_pending: 0,
        count_overdue: 0,
      }

      data?.forEach((p: { amount: number; status: string }) => {
        const amount = Number(p.amount) || 0
        if (p.status === 'paid') {
          summary.total_paid += amount
          summary.count_paid++
        } else if (p.status === 'pending') {
          summary.total_pending += amount
          summary.count_pending++
        } else if (p.status === 'overdue') {
          summary.total_overdue += amount
          summary.count_overdue++
        }
      })

      return summary
    },
    enabled: !!user && !!projectId,
  })
}

export function useCreatePayment() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (payment: PaymentInsert) => {
      if (!user) throw new Error('Usuário não autenticado')

      const { data, error } = await supabase
        .from('payments')
        .insert({
          ...payment,
          user_id: user.id,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Pagamento registrado com sucesso!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao registrar pagamento: ${error.message}`)
    },
  })
}

export function useCreateBulkPayments() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (payments: PaymentInsert[]) => {
      if (!user) throw new Error('Usuário não autenticado')

      const paymentsWithUserId = payments.map(p => ({
        ...p,
        user_id: user.id,
      }))

      const { data, error } = await supabase
        .from('payments')
        .insert(paymentsWithUserId)
        .select()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(`${data.length} parcela(s) criada(s) com sucesso!`)
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar parcelas: ${error.message}`)
    },
  })
}

export function useUpdatePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PaymentInsert> & { id: string }) => {
      const { data, error } = await supabase
        .from('payments')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['payments', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Pagamento atualizado!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar pagamento: ${error.message}`)
    },
  })
}

export function useMarkPaymentAsPaid() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (id: string) => {
      // First, get the payment to check if it's a monthly recurring payment
      const { data: payment, error: fetchError } = await supabase
        .from('payments')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      // Update the payment to paid
      const { data, error } = await supabase
        .from('payments')
        .update({
          status: 'paid',
          paid_date: new Date().toISOString().split('T')[0],
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      // If it's a monthly recurring payment, create the next one
      if (payment.recurring_day_of_month && user) {
        const today = new Date()
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
        const dayOfMonth = Math.min(payment.recurring_day_of_month, new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate())
        const nextDueDate = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), dayOfMonth)

        // Create the next recurring payment
        await supabase
          .from('payments')
          .insert({
            user_id: user.id,
            project_id: payment.project_id,
            client_id: payment.client_id,
            amount: payment.amount,
            currency: payment.currency,
            due_date: nextDueDate.toISOString().split('T')[0],
            status: 'pending',
            payment_method: payment.payment_method,
            description: payment.description,
            notes: payment.notes,
            recurring_day_of_month: payment.recurring_day_of_month,
            recurring_parent_id: payment.recurring_parent_id || payment.id,
          })
      }

      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      
      // Check if next payment was created
      if (data?.recurring_day_of_month) {
        toast.success('Pagamento marcado como pago! Próximo mês criado automaticamente 🎉')
      } else {
        toast.success('Pagamento marcado como pago! 🎉')
      }
    },
    onError: (error: Error) => {
      toast.error(`Erro ao marcar como pago: ${error.message}`)
    },
  })
}

export function useDeletePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Pagamento excluído!')
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir pagamento: ${error.message}`)
    },
  })
}
